import modal from '~components/modal';
import { CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from 'antd';
import { displayLocaleOptions } from '~constants/property-field-options';
import { dayFormatType, formatDay } from '~helpers/day';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { formatSeconds, calcIsCirculation } from '../notification-editor';

const NotificationCombinedByEnum = {
  _AND: 'cms.marketing.notification.viewer.item.combined_by.and',
  _OR: 'cms.marketing.notification.viewer.item.combined_by.or',
};

const NotificationChannelEnum = {
  APP: 'cms.marketing.notification.viewer.item.channel.app',
};

const NotificationRedirectTypeEnum = {
  PP: 'cms.marketing.notification.viewer.item.landing_page_type.pp',
  LANDING_PAGE: 'cms.marketing.notification.viewer.item.landing_page_type.landing_page',
};

const findLocaleLabel = locale => {
  return displayLocaleOptions.find(option => option.value === locale)?.name || locale;
};

const getFormatDateTime = (isCirculation, publishStart, publishEnd) => {
  if (publishStart && publishEnd && isCirculation) {
    return `${publishStart ? formatDay(dayFormatType.UKWITHHMS, publishStart) : '-'} - ${
      publishEnd ? formatDay(dayFormatType.UKWITHHMS, publishEnd) : '-'
    }`;
  }

  if (publishStart) {
    return formatDay(dayFormatType.UKWITHHMS, publishStart);
  }

  return '-';
};

const getFormatOppLostLabel = oppLost => {
  if (!oppLost || !oppLost.lost) {
    return '-';
  }
  if (oppLost.start && oppLost.end) {
    return `${formatDay(dayFormatType.UKWITHHMS, oppLost.start)} - ${formatDay(
      dayFormatType.UKWITHHMS,
      oppLost.end,
    )}`;
  }
  return 'Yes';
};

function NotificationViewer(props) {
  const { handleCloseModal, viewingNotification, openEditor } = props;
  const { t } = useTranslation();
  const { data: queryCityResponse } = useQuery(
    gql`
      query queryCity($slug: String) {
        city(slug: $slug) {
          name
        }
      }
    `,
    {
      variables: {
        slug: viewingNotification.triggerCondition.destinationCity,
      },
      fetchPolicy: 'no-cache',
    },
  );

  return (
    <div className="notification-viewer">
      <div className="notification-viewer__header modal__header">
        {t(`cms.marketing.notification.viewer.title`)}
        <CloseOutlined className="modal__close-button" onClick={handleCloseModal} />
      </div>
      <div className="notification-viewer__body modal__body">
        <section className="notification-viewer__section">
          <h3 className="notification-viewer__section__title">
            {t('cms.marketing.notification.viewer.section.trigger_point')}
          </h3>
          <TableItem
            title={t('cms.marketing.notification.viewer.item.filters_can_be_combined_by')}
            description={t(NotificationCombinedByEnum[viewingNotification.combinedBy])}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.not_register')}
            description={
              <>
                {viewingNotification.triggerCondition.registered === false && (
                  <Icon className="notification-viewer__positive-check" type="check" />
                )}
                {viewingNotification.triggerCondition.registered === false ? 'Yes' : '-'}
              </>
            }
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.destination_city')}
            description={queryCityResponse?.city?.name || '-'}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.student_language')}
            description={viewingNotification.triggerCondition.locales
              .map(locale => findLocaleLabel(locale.replace('_', '-')))
              .join('，')}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.opp_lost')}
            description={getFormatOppLostLabel(viewingNotification.triggerCondition.oppLost)}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.active_time')}
            description={
              viewingNotification.triggerCondition.activeTime
                ? `${formatDay(
                    dayFormatType.UKWITHHMS,
                    viewingNotification.triggerCondition.activeTime.start,
                  )} - ${
                    viewingNotification.triggerCondition.activeTime.end
                      ? formatDay(
                          dayFormatType.UKWITHHMS,
                          viewingNotification.triggerCondition.activeTime.end,
                        )
                      : 'Infinity'
                  }`
                : '-'
            }
          />
        </section>

        <section className="notification-viewer__section">
          <h3 className="notification-viewer__section__title">
            {t('cms.marketing.notification.viewer.section.message_setting')}
          </h3>
          <TableItem
            title={t('cms.marketing.notification.viewer.item.notification_name')}
            description={viewingNotification.name}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.channel')}
            description={viewingNotification.channels
              .map(channel => t(NotificationChannelEnum[channel]))
              .join('，')}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.message_content')}
            description={viewingNotification.messageContent}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.wait_time')}
            description={
              viewingNotification.waitTime
                ? `
            ${formatSeconds(viewingNotification.waitTime).days} Days 
            ${formatSeconds(viewingNotification.waitTime).hours} Hours 
            ${formatSeconds(viewingNotification.waitTime).minutes} Minutes `
                : '-'
            }
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.schedule_publish_time')}
            description={getFormatDateTime(
              calcIsCirculation(viewingNotification.publishType),
              viewingNotification.publishStart,
              viewingNotification.publishEnd,
            )}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.if_impact_on_the_future')}
            description={
              <>
                {calcIsCirculation(viewingNotification.publishType) ? (
                  <>
                    <Icon className="notification-viewer__positive-check" type="check" />
                    {t('cms.marketing.notification.editor.is_circulation.yes')}
                  </>
                ) : (
                  t('cms.marketing.notification.editor.is_circulation.no')
                )}
              </>
            }
          />
        </section>

        <section className="notification-viewer__section">
          <h3 className="notification-viewer__section__title">
            {t('cms.marketing.notification.viewer.section.landing_page')}
          </h3>
          <TableItem
            title={t('cms.marketing.notification.viewer.item.landing_page_type')}
            description={t(NotificationRedirectTypeEnum[viewingNotification.redirectType])}
          />
          <TableItem
            title={t('cms.marketing.notification.viewer.item.landing_page_url')}
            description={viewingNotification.redirectValue}
          />
        </section>
      </div>

      <div className="notification-viewer__footer modal__footer">
        <Button size="large" icon="edit" onClick={openEditor}>
          {t('cms.marketing.notification.viewer.button.edit')}
        </Button>
      </div>
    </div>
  );
}

function TableItem({ title, description }) {
  return (
    <div className="notification-viewer__table-item">
      <div className="notification-viewer__table-item__title-container">
        <h5 className="notification-viewer__table-item__title">{title}</h5>
      </div>
      <div className="notification-viewer__table-item__context-container">
        <p className="notification-viewer__table-item__context">{description}</p>
      </div>
    </div>
  );
}

NotificationViewer.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  viewingNotification: PropTypes.object.isRequired,
  openEditor: PropTypes.func.isRequired,
};

NotificationViewer.defaultProps = {
  handleCloseModal: () => {},
  viewingNotification: {},
  openEditor: () => {},
};

export default modal(
  {
    useDefaultStyle: true,
  },
  true,
)(NotificationViewer);
